/**
 * Список подання платформи (LST-01, E4a-W1): подання з `/meta`, колонки й підписи — з метаданих,
 * дані — курсорними порціями з `/api/v1/records` за правами людини. Таблиця — спільне ядро
 * `features/list/RecordTable.tsx` (віртуалізація, курсор, стани, клавіатура); тут — панель екрана,
 * стани подання (не знайдено, порожньо, помилка) і «Створити …» — швидке створення сутності подання
 * (DAT-01, діалог `create/QuickCreateDialog.tsx`). Пошук — провайдери
 * модулів (`/api/v1/search`). Оновлення — SSE (events.ts).
 *
 * Адреси й повернення (UX-05…08): рядок — справжнє посилання на картку з контекстом подання у
 * стані історії (крихта, «N з M», `J` / `K`); повернення до списку — на те саме місце й рядок.
 * Без входу (401) — на вхід з поверненням сюди.
 *
 * E4a-W2 (LST-02, 04, 10, 15, 16, 18): лічильник біля назви (`view-count.ts`), кнопки «Стовпці» і
 * «↻ N» у панелі. Важке — лінивими модулями після першого екрана (`afterPrefetchStart`) чи на намір
 * людини: сортування в шапці (`features/list/SortHeader.tsx`), панель стовпців
 * (`features/list/ColumnsPanel.tsx`), «Подання надто важке» (`features/list/HeavyState.tsx`, лише
 * при `query_timeout`) і живе оновлення (`list-live.ts`; у легкому режимі — за 60 с, опитуванням).
 *
 * E4a-W3 (FLT-02, 03, 06, 07, 08; LST-03): сортування з шапки — незбережена зміна подання в адресі
 * (`?d=`, view-draft.ts): посиланням ділиться, «Назад» не засмічує (`replace`). Назва — перемикач
 * подань, поруч «• змінено» і «Зберегти» (view-bar.tsx). Ширини стовпців — особистий шар подання
 * (view-prefs.ts). Чипи — закріплені людиною подання сутності; не закріплено — подання ролі й
 * платформи.
 *
 * E4a-W4 (FLT-10, 11, 12…15, 18): умова подання (view-config.ts) — у запиті даних і чипами в панелі
 * (filters/filter-bar.tsx); зміна умови — та сама чернетка `?d=`. Умова з полями `personal` /
 * `sensitive` в адресу не йде (SEC-110): тримається в стані екрана, а посилання — без неї. Поки
 * новий фільтр у дорозі — старі рядки приглушені й смужка над таблицею; лічильник з чернеткою
 * фільтра не показується (він рахує подання, а не чернетку).
 */

import type {
  AppMeta,
  BulkSelection,
  FilterNode,
  MetaEntity,
  MetaView,
  RecordRow,
  ViewSort,
} from '@clientline/contracts';
import {
  cleanSearchText,
  SEARCH_QUERY_MAX,
  searchQueryTooShort,
} from '@clientline/contracts/text-rules';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouterState, useSearch } from '@tanstack/react-router';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { listScreenKey } from '../app/return-state.ts';
import { useRoutePending } from '../app/route-pending.ts';
import { usePageTitle } from '../brand/env.ts';
import type { ColumnSpec } from '../features/list/columns.ts';
import {
  isQueryTimeout,
  RecordTable,
  type RowMenuContext,
  type TableSelection,
} from '../features/list/RecordTable.tsx';
import { loadMyTasks } from '../features/tasks/my/load.ts';
import { taskViewKey } from '../features/tasks/views.ts';
import { type WorkPanelState, workAccess } from '../features/tasks/work-access.ts';
import { TodayToggle, useTodayToggleItem } from '../features/today/TodayToggle.tsx';
import { plural, useRegionKey } from '../i18n/format.ts';
import { filters as tf } from '../i18n/uk/filters.ts';
import { list as tl } from '../i18n/uk/list.ts';
import { navigation as n } from '../i18n/uk/navigation.ts';
import { openPalette } from '../palette/store.ts';
import { useFirstScreenDone } from '../perf/first-screen.ts';
import { afterPrefetchStart } from '../perf/prefetch.ts';
import { useCapabilities, usePrefs } from '../prefs/prefs.ts';
import { ErrorState } from '../support/ErrorState.tsx';
import { useAreaState } from '../ui/Area.tsx';
import { createLabel, EmptyState } from '../ui/EmptyState.tsx';
import type { MenuItem } from '../ui/Menu.tsx';
import { announce, toast } from '../ui/notify/store.ts';
import { Pending } from '../ui/Pending.tsx';
import { RetryBoundary } from '../ui/RetryBoundary.tsx';
import { RoutePending } from '../ui/RoutePending.tsx';
import { SkeletonLines, SkeletonTable } from '../ui/Skeleton.tsx';
import { StateView } from '../ui/StateView.tsx';
import { BULK_IDS_MAX } from './bulk/limits.ts';
import { busyMessage } from './busy.ts';
import { holdListRefresh } from './events.ts';
import { useFilterBar } from './filters/filter-bar.tsx';
import { RefsProvider } from './LookupValue.tsx';
import { historyIndexOf, type ListContext } from './list-context.ts';
import type { LiveList, LiveSnapshot } from './list-live.ts';
import {
  fetchListPage,
  type ListParams,
  listParamsOf,
  listSort,
  platformListQuery,
  usePlatformListSource,
  viewColumns,
} from './list-source.tsx';
import { refIndex } from './lookup-refs.ts';
import {
  entityOf,
  fieldOf,
  isUnauthorized,
  mergeQueueEntities,
  OWNER_KEY,
  recordTitle,
  usePlatformMeta,
} from './meta.ts';
import { openQuickCreate, useQuickCreateRequest } from './quick-create.ts';
import { useSignInRedirect } from './sign-in.ts';
import { platformText as t } from './text.ts';
import {
  useAddressDraft,
  useViewDraft,
  type ViewDraftScreen,
  withFilterDraft,
} from './use-view-draft.ts';
import { useViewBar } from './view-bar.tsx';
import {
  isViewUnavailable,
  scopeConfig,
  seedStartConfig,
  type ViewQueryConfig,
  viewConfigQuery,
} from './view-config.ts';
import { countLabel, exactCount, viewCountQuery, viewCountsKey } from './view-count.ts';
import type { DecodedDraft, ViewDraft } from './view-draft.ts';
import { ViewSwitch } from './view-layout.tsx';
import {
  flushViewWidths,
  migrateLocalColumns,
  saveViewColumns,
  saveViewWidth,
} from './view-prefs.ts';
import { chipViews, menuGroup, ViewToolbar } from './view-toolbar.tsx';

// Ліниві модулі списку (E4a-W2). Імпорт повторюється на кожну спробу: відхилений `import()`
// браузер не кешує, тож збій мережі не ламає модуль назавжди.
const loadSortHeader = () => import('../features/list/SortHeader.tsx');
const loadColumnsPanel = () => import('../features/list/ColumnsPanel.tsx');
const loadLive = () => import('./list-live.ts');
const columnsPanelChunk = () => lazy(loadColumnsPanel);
const heavyChunk = () => lazy(() => import('../features/list/HeavyState.tsx'));

/**
 * Живе оновлення без першого екрана (LST-18): після `prefetch:start`; якщо планувальник не
 * запускався (легкий режим, «Економія трафіку»), — за хвилину, і далі опитування раз на 60 с.
 */
export const LIVE_START_FALLBACK_MS = 60_000;

const NO_LIVE: LiveSnapshot = { marks: new Map(), pending: 0 };
const noSubscribe = () => () => undefined;
const noSnapshot = () => NO_LIVE;
type SortModule = Awaited<ReturnType<typeof loadSortHeader>>;
const LIST_SCREEN: ViewDraftScreen = { route: '/l/$view' };
/**
 * Хто з дій панелі займає місце першим (ViewToolbar): основна дія, «Зберегти» змін, фільтр; що не
 * вміщується, — у «⋯» перед «Створити …» (аудит якості № 11). Лупа пошуку закріплена й у «⋯» не
 * йде (рішення власника 2026-10-28, SRC-15).
 */
const LIST_ACTIONS = [
  'create',
  'view-save',
  'filter',
  'view-menu',
  'refresh',
  'view-save-as',
  'view-reset',
  'columns',
  'today',
];

/**
 * Пошук списку (SRC-15; рішення власника 2026-10-28): у панелі — лупа, що розгортається в поле на
 * місці, а не пункт «⋯». `Enter` робить текст чипом подання. `Esc` чи втрата фокусу з порожнім полем
 * згортають його назад у лупу (після `Esc` фокус — на лупі); з запитом поле лишається розгорнутим.
 */
export function SearchBox({ onSearch }: { onSearch: (text: string) => void }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  // Куди фокус після розгортання чи згортання: у поле, на лупу або нікуди.
  // Після кожного коміту: ставить його лише клік по лупі чи `Esc`, тож зайвого перенесення немає.
  const focusNext = useRef<'input' | 'toggle' | null>(null);
  useLayoutEffect(() => {
    if (!focusNext.current) return;
    (focusNext.current === 'input' ? input.current : toggle.current)?.focus();
    focusNext.current = null;
  });
  const collapse = (refocus: boolean) => {
    focusNext.current = refocus ? 'toggle' : null;
    setQ('');
    setOpen(false);
  };
  if (!open) {
    return (
      <button
        ref={toggle}
        className="btn btn--ghost btn--icon"
        type="button"
        aria-label={t.search}
        title={t.search}
        onClick={() => {
          focusNext.current = 'input';
          setOpen(true);
        }}
        data-testid="search-toggle"
      >
        <i className="ic ic-search" aria-hidden="true" />
      </button>
    );
  }
  return (
    <div className="p-search">
      <input
        ref={input}
        className="input"
        type="search"
        aria-label={t.search}
        placeholder={t.search}
        value={q}
        maxLength={SEARCH_QUERY_MAX}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return;
          // `Enter` робить текст чипом подання (SRC-15); `Esc` — очистити, з порожнім — згорнути.
          if (e.key === 'Enter') {
            e.preventDefault();
            const text = cleanSearchText(q);
            if (text) {
              onSearch(text);
              collapse(true);
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            if (q !== '') setQ('');
            else collapse(true);
          }
        }}
        onBlur={() => {
          if (q.trim() === '') collapse(false);
        }}
        data-testid="search"
      />
    </div>
  );
}

/** Чип пошуку в поданні (SRC-15): поряд із чипами фільтра, хрестик прибирає лише пошук. */
function SearchChip({ q, onClear }: { q: string; onClear: () => void }) {
  return (
    <span className="chip chip--active chip--filter" data-testid="search-chip">
      <span className="chip__body" title={q}>
        {t.searchChip(q)}
      </span>
      <button
        type="button"
        className="chip__x"
        aria-label={t.searchChipClear}
        title={t.searchChipClear}
        onClick={onClear}
        data-testid="search-chip-clear"
      >
        <i className="ic ic-close" aria-hidden="true" />
      </button>
    </span>
  );
}

export function ListPage() {
  useRegionKey();
  const { view } = useParams({ from: '/l/$view' });
  const meta = usePlatformMeta();
  const viewMeta = meta.data?.views.find((v) => v.key === view);
  const entity = meta.data && viewMeta ? entityOf(meta.data, viewMeta.entity) : undefined;
  // Заголовок вкладки — як на екранах S4 (бренд-бук, розд. 7): назва подання.
  usePageTitle(viewMeta?.label.uk ?? (meta.isPending ? undefined : t.notFound));
  const unauthorized = isUnauthorized(meta.error);
  useSignInRedirect(unauthorized);

  if (unauthorized || meta.isPending) return null;
  if (!viewMeta || !entity || !meta.data) return <StateView icon="inbox" title={t.notFound} />;
  const otherViews = meta.data.views.filter((v) => v.entity === entity.key);
  // Подання «Мої задачі» (E8a-W2): умова з якорем індексу поведінки — лінивий екран задач.
  if (taskViewKey(viewMeta, entity)) {
    return <TaskViews view={viewMeta} entity={entity} views={otherViews} />;
  }
  // Ключ — подання: інший `/l/<view>` — свіжий стан таблиці (фокус, вибір, прокрутка).
  return (
    <ViewList
      key={view}
      view={viewMeta}
      entity={entity}
      views={otherViews}
      meta={meta.data}
      canDelete={meta.data.permissions.includes(`${entity.key}.delete`)}
      canBulk={meta.data.permissions.includes(`${entity.key}.bulk`)}
      canExport={meta.data.permissions.includes(`${entity.key}.export`)}
      canUpdate={meta.data.permissions.includes(`${entity.key}.update`)}
      canTransfer={
        fieldOf(entity, OWNER_KEY) !== undefined &&
        meta.data.permissions.includes(`${entity.key}.transfer`)
      }
    />
  );
}

/**
 * «Мої задачі» — лінивий чанк (форма задачі в ньому ж): на перший екран не впливає. Відхилений
 * імпорт (обрив мережі, нова збірка) — «Спробувати ще раз» з новим `lazy()`, бо React кешує збій.
 */
/** Діалог видалення одного запису (DAT-04) — лінивий чанк, вантажиться після «Видалити…». */
const DeleteFlow = lazy(() => import('./trash/DeleteFlow.tsx'));
type RowMenuItems = typeof import('./list-row-menu.ts')['rowMenuItems'];
let loadedRowMenu: RowMenuItems | null = null;
let rowMenuModule: Promise<RowMenuItems> | null = null;
/** Пункти «⋯» рядка (LST-23) — лінивий модуль, з першим відкриттям меню; збій не запам'ятовуємо. */
function loadRowMenu(): Promise<RowMenuItems> {
  rowMenuModule ??= import('./list-row-menu.ts').then(
    (m) => (loadedRowMenu = m.rowMenuItems),
    (error: unknown) => {
      rowMenuModule = null;
      throw error;
    },
  );
  return rowMenuModule;
}
// «Записати результат» з рядка (LST-23) — той самий корпус панелі, що на дошці (KAN-11).
const WorkPanelBody = lazy(() =>
  import('../features/tasks/work-panel-body.tsx').then((m) => ({ default: m.WorkPanelBody })),
);
/** Масові дії (LST-26…28, LST-31) — лінивий чанк: на перший екран не впливає. */
type BulkFilter = NonNullable<Extract<BulkSelection, { kind: 'query' }>['filter']>;
const BulkFlow = lazy(() => import('./bulk/BulkFlow.tsx'));
const BulkEditFlow = lazy(() => import('./bulk/BulkEditFlow.tsx'));
const BulkTransferFlow = lazy(() => import('./bulk/BulkTransferFlow.tsx'));
const ExportFlow = lazy(() => import('./bulk/ExportFlow.tsx'));

const myTasksChunk = () => lazy(() => loadMyTasks());
/** Один лінивий компонент на вкладку: перемикання подань не показує скелетон знову. */
let myTasks = myTasksChunk();

function TaskViews(props: { view: MetaView; entity: MetaEntity; views: readonly MetaView[] }) {
  const [Page, setPage] = useState(() => myTasks);
  return (
    <RetryBoundary
      onRetry={() => {
        myTasks = myTasksChunk();
        setPage(() => myTasks);
      }}
      fallback={(retry) => (
        <StateView icon="alert-circle" tone="error" title={t.error}>
          <button className="btn" type="button" onClick={retry}>
            {t.retry}
          </button>
        </StateView>
      )}
    >
      <Suspense fallback={<ListSkeleton />}>
        <Page {...props} />
      </Suspense>
    </RetryBoundary>
  );
}

interface ViewListProps {
  view: MetaView;
  entity: MetaEntity;
  views: readonly MetaView[];
  /** Знімок `/meta` для діалогу видалення — значенням, без спостерігача (див. вище). */
  meta: AppMeta;
  /** Роль має `<e>.delete`: «Видалити…» в панелі вибору одного рядка (DAT-04). */
  canDelete: boolean;
  /** Роль має `<e>.bulk`: масові дії над обраними (LST-24…26); без нього й `delete` чекбоксів немає. */
  canBulk: boolean;
  /** `<e>.export` (LST-32): «Експорт…» обраних і «Експорт» усіх за фільтром. */
  canExport: boolean;
  /** `<e>.update` разом із `bulk`: «Змінити поля…» (LST-27). */
  canUpdate: boolean;
  /** `<e>.transfer` разом із `bulk`, у сутності є поле відповідального: LST-28. */
  canTransfer: boolean;
}

/**
 * Умова й сортування подання (views.md, розд. 6): сервер їх не підставляє, клієнт шле сам. Зазвичай
 * уже в кеші (`loader` маршруту чи `meta.start`); з фільтром у чернетці список не чекає на неї.
 * Подання недоступне людині — стан «недоступне», інший збій — помилка з повтором.
 */
function ViewList(props: ViewListProps) {
  const client = useQueryClient();
  const { view, meta } = props;
  useState(() => seedStartConfig(client, meta));
  // Незбережені зміни подання (FLT-08): з адреси (`?d=`) або зі стану на сервері (`?state=`), той
  // самий розбір, що й у `loader` маршруту. Стан у дорозі — скелет (зазвичай його вже взяв `loader`).
  const { d, state } = useSearch({ from: '/l/$view' });
  const { decoded, fromState } = useAddressDraft(view.id, d, state);
  const draftFilter = decoded?.ok === true && decoded.draft.filter !== undefined;
  const config = useQuery({
    ...viewConfigQuery(view.id ?? '', view.ownerKind),
    enabled: view.id !== undefined,
  });
  const scoped = useMemo(() => scopeConfig(view), [view]);
  if (!decoded) {
    if (fromState.error && !fromState.isFetching) {
      return (
        <ErrorState
          error={fromState.error}
          onRetry={() => void fromState.refetch()}
          title={t.error}
        />
      );
    }
    return <ListSkeleton />;
  }
  if (!view.id) return <ViewListBody {...props} decoded={decoded} config={scoped} />;
  if (config.data) return <ViewListBody {...props} decoded={decoded} config={config.data} />;
  if (isViewUnavailable(config.error)) {
    return <StateView icon="lock" title={tf.viewUnavailable} />;
  }
  if (draftFilter) return <ViewListBody {...props} decoded={decoded} config={undefined} />;
  if (config.error) {
    return (
      <ErrorState error={config.error} onRetry={() => void config.refetch()} title={t.error} />
    );
  }
  return <ListSkeleton />;
}

/**
 * Подання зі списком. Метаданих тут не читаємо (їх дає `ListPage`): ця частина перемальовується
 * від налаштувань (розмір порції), а спостерігач `/meta` при перемальовуванні під час «Вийти»
 * перезапитав би метадані після `queryClient.clear()` і повернув ознаку входу (sign-out.ts, #408).
 */
function ViewListBody({
  view,
  entity,
  views,
  meta,
  canDelete,
  canBulk,
  canExport,
  canUpdate,
  canTransfer,
  config,
  decoded,
}: ViewListProps & {
  /** Умова й сортування подання; `undefined` — ще в дорозі (діє фільтр з чернетки). */
  config: ViewQueryConfig | undefined;
  /** Незбережені зміни з адреси чи стану на сервері (`ViewList`). */
  decoded: DecodedDraft;
}) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const { pageSize, lite } = useCapabilities();
  const { listColumns } = usePrefs();
  const [refreshTick, setRefreshTick] = useState(0);
  // Незбережені зміни подання — з адреси (`?d=`) чи стану на сервері (`?state=`, розбір — `ViewList`).
  const { d, state } = useSearch({ from: '/l/$view' });
  // Одна машина стану чернетки з дошкою (`use-view-draft.ts`): правило SEC-110 — в одному місці.
  const {
    draft,
    held: heldNow,
    open,
  } = useViewDraft(
    view,
    entity,
    d,
    LIST_SCREEN,
    state === undefined ? undefined : { state, decoded },
  );
  // Фільтр поза адресою: у пам'яті вкладки чи в стані на сервері (контекст картки — нижче).
  const offUrl = heldNow || state !== undefined;
  const params = useMemo<ListParams>(
    () => listParamsOf(view, entity, { listColumns }, draft, config),
    [view, entity, listColumns, draft, config],
  );
  // Що діє насправді: сортування, якого сервер не прийме чи яке збігається з поданням, — не зміна.
  const shown = useMemo<ViewDraft>(() => {
    const sort =
      params.sort &&
      (draft.q || params.sort.field !== view.sort.field || params.sort.dir !== view.sort.dir)
        ? params.sort
        : undefined;
    return {
      ...(sort ? { sort } : {}),
      ...(draft.filter !== undefined ? { filter: draft.filter } : {}),
      ...(draft.q ? { q: draft.q } : {}),
    };
  }, [params.sort, view.sort, draft.filter, draft.q]);
  // Ширини, що ще чекають запису, — на сервер, коли людина йде з подання.
  useEffect(() => () => flushViewWidths(client), [client]);
  const query = useMemo(
    () => platformListQuery(view, entity, pageSize, params),
    [view, entity, pageSize, params],
  );
  // Той самий кеш, що й у таблиці: контекст для картки й відмова 401 (без окремого запиту).
  const list = useInfiniteQuery(query);
  const searchNote = list.data?.pages[0]?.searchNote;
  // Видалення одного обраного рядка (DAT-04; trash.html, кадр Б1): той самий діалог, що й у картці.
  const [deleting, setDeleting] = useState<{
    id: string;
    title: string | undefined;
    done: () => void;
  } | null>(null);
  // Масові дії (LST-27, 28, 31): вибір таблиці → вибір запиту з тим самим фільтром, що й список.
  // Номер запуску — `key` потоку: новий запуск — новий екземпляр, ключ дії й діалог (рев'ю Security
  // #1023, M1). Поки задача попереднього запуску в дорозі (`running`), нового не почати: інакше
  // демонтаж зняв би відлік, прогрес і звіт задачі в дорозі (#1027, S1).
  const runs = useRef(0);
  const [bulkRun, setBulkRun] = useState<{
    kind: 'delete' | 'edit' | 'transfer';
    run: number;
    selection: BulkSelection;
    selected: number | null;
    clear: () => void;
    running?: boolean;
  } | null>(null);
  // Експорт (LST-32): обрані чи всі за фільтром; панель і тост живуть, доки не почнуть новий
  // (тоді тост «Завантажити» попереднього прибирає сам потік), а в дорозі новий не почати.
  const [exporting, setExporting] = useState<{
    run: number;
    selection: BulkSelection;
    clear: () => void;
    running?: boolean;
  } | null>(null);
  const bulkRunning = bulkRun?.running === true;
  const exportRunning = exporting?.running === true;
  /** Запуск `run` почав задачу чи закінчив її: прапорець «в дорозі» лише свого запуску. */
  const markRunning =
    <T extends { run: number; running?: boolean }>(run: number, running: boolean) =>
    (state: T | null): T | null =>
      state && state.run === run ? { ...state, running } : state;
  // Масова задача за умовою не знає пошуку подання (SRC-15): «усі за умовою» зачепили б і те, чого
  // людина не бачить на екрані, тож під пошуком — лише явно обрані рядки.
  const toBulk = (selection: TableSelection): BulkSelection | null =>
    selection.kind === 'query'
      ? params.q
        ? null
        : {
            kind: 'query',
            ...(params.filter ? { filter: params.filter as BulkFilter } : {}),
            excludeIds: [...selection.excludeIds],
          }
      : selection.ids.length <= BULK_IDS_MAX
        ? { kind: 'ids', ids: [...selection.ids] }
        : null;
  const bulkBlocked = params.q ? tl.bulkUnderSearch : tl.bulkTooMany;
  /** Масова задача над обраними: нова — лише коли попередня дійшла кінця. */
  const bulkAction = (
    kind: 'delete' | 'edit' | 'transfer',
    selection: TableSelection,
    clear: () => void,
  ) => {
    const bulk = toBulk(selection);
    return {
      disabled: bulk === null || bulkRunning,
      title: bulk === null ? bulkBlocked : bulkRunning ? tl.bulkRunning : undefined,
      onClick: () => {
        if (!bulk || bulkRunning) return;
        setBulkRun({
          kind,
          run: ++runs.current,
          selection: bulk,
          selected: selection.kind === 'ids' ? selection.ids.length : selection.count,
          clear,
        });
      },
    };
  };
  // Дії над обраними (LST-24…28, 32): видалення одного — за `delete` (діалог з наслідками), кількох
  // чи «усіх за фільтром» — масовою задачею за `delete` і `bulk`; зміна полів і відповідального — за
  // `bulk` і ключем дії; експорт — за `export`.
  const deleteAction = (selection: TableSelection, clear: () => void) => {
    if (!canDelete) return null;
    const one =
      selection.kind === 'ids' && selection.ids.length === 1 ? selection.ids[0] : undefined;
    if (one) {
      return (
        <button
          className="btn btn--sm bulkbar__danger"
          type="button"
          onClick={() => {
            const row = list.data?.pages.flatMap((p) => p.items).find((r) => r.id === one);
            setDeleting({
              id: one,
              title: row ? recordTitle(entity, row.values) : undefined,
              done: clear,
            });
          }}
          data-testid="list-delete"
        >
          {tl.deleteOne}
        </button>
      );
    }
    if (!canBulk) return null;
    return (
      <button
        className="btn btn--sm bulkbar__danger"
        type="button"
        {...bulkAction('delete', selection, clear)}
        data-testid="list-bulk-delete"
      >
        {tl.deleteMany}
      </button>
    );
  };
  const exportAction = (selection: TableSelection, clear: () => void) => {
    if (!canExport) return null;
    const bulk = toBulk(selection);
    return (
      <button
        className="btn btn--sm"
        type="button"
        disabled={bulk === null || exportRunning}
        title={bulk === null ? bulkBlocked : exportRunning ? tl.bulkRunning : undefined}
        onClick={() => {
          if (bulk && !exportRunning) setExporting({ run: ++runs.current, selection: bulk, clear });
        }}
        data-testid="list-bulk-export"
      >
        {tl.exportMany}
      </button>
    );
  };
  const selectionActions =
    canDelete || canBulk || canExport
      ? (selection: TableSelection, clear: () => void) => (
          <>
            {canBulk && canTransfer && (
              <button
                className="btn btn--sm"
                type="button"
                {...bulkAction('transfer', selection, clear)}
                data-testid="list-bulk-transfer"
              >
                {tl.transferMany}
              </button>
            )}
            {canBulk && canUpdate && (
              <button
                className="btn btn--sm"
                type="button"
                {...bulkAction('edit', selection, clear)}
                data-testid="list-bulk-edit"
              >
                {tl.editMany}
              </button>
            )}
            {exportAction(selection, clear)}
            {deleteAction(selection, clear)}
          </>
        )
      : undefined;
  const exportAll = () => {
    if (exportRunning) {
      toast({ key: `export-running-${entity.key}`, text: tl.bulkRunning });
      return;
    }
    setExporting({
      run: ++runs.current,
      selection: {
        kind: 'query',
        ...(params.filter ? { filter: params.filter as BulkFilter } : {}),
        excludeIds: [],
      },
      clear: () => undefined,
    });
  };
  // Діалог швидкого створення відкритий з цього списку (DAT-01): порожній стан його не дублює.
  const creating = useQuickCreateRequest()?.entity === entity.key;
  const create = () => openQuickCreate({ entity: entity.key });
  // Знімок порядку для картки: усі завантажені порції й курсор наступної. `from` — запис історії
  // цього списку: картка повертається «Назад» лише на нього (UX-05).
  const listIndex = useRouterState({ select: (s) => historyIndexOf(s.location.state) });
  const context = useRef<ListContext | undefined>(undefined);
  // Сортування й умова — ті, з якими картка дочитає наступну порцію (`extendContext`). Фільтр, що
  // не йде в адресу (і той, що в стані на сервері), не йде й у стан історії: картка гортає лише
  // завантажене.
  const contextSort = listSort(view, params);
  context.current =
    list.data && !list.isPlaceholderData
      ? {
          view: view.key,
          ids: list.data.pages.flatMap((p) => p.items.map((r) => r.id)),
          cursor: offUrl ? null : (list.data.pages.at(-1)?.nextCursor ?? null),
          ...(contextSort ? { sort: `${contextSort.field}:${contextSort.dir}` } : {}),
          ...(params.filter !== undefined && !offUrl ? { filter: params.filter } : {}),
          ...(listIndex !== undefined ? { from: listIndex } : {}),
        }
      : undefined;
  const source = usePlatformListSource(view, entity, context, params);

  // Меню «⋯» рядка (LST-23): пункти — за правами, у момент відкриття; «Записати результат» (і `L`)
  // — та сама панель, що на дошці й у картці, поверх списку.
  const [work, setWork] = useState<WorkPanelState | null>(null);
  const access = workAccess(meta, entity);
  const hostOf = (row: RecordRow) => ({
    entity: entity.key,
    id: row.id,
    // Тема за замовчуванням — `recordTitle`: без полів `sensitive` і контактних (рев'ю Security #612).
    title: recordTitle(entity, row.values) ?? '',
  });
  const logRow = access.log
    ? (row: RecordRow) => setWork({ kind: 'log', host: hostOf(row) })
    : undefined;
  const [rowItems, setRowItems] = useState<RowMenuItems | null>(() => loadedRowMenu);
  const rowMenu = (row: RecordRow, ctx: RowMenuContext): MenuItem[] | null =>
    rowItems?.({
      entity: entity.key,
      id: row.id,
      open: () => source.open(row),
      href: source.href(row),
      log: logRow && (() => logRow(row)),
      remove: canDelete
        ? () =>
            setDeleting({
              id: row.id,
              title: recordTitle(entity, row.values),
              done: ctx.followGone,
            })
        : undefined,
    }) ?? null;
  const onRowMenuOpen = () => loadRowMenu().then((fn) => setRowItems(() => fn));
  /** Панель закрито: фокус — на рядок, з якого її відкрили. */
  const closeWork = () => {
    const id = work?.host.id;
    setWork(null);
    if (id)
      requestAnimationFrame(() =>
        document.querySelector<HTMLElement>(`tr[data-id="${CSS.escape(id)}"]`)?.focus(),
      );
  };
  // Назви цілей полів-зв'язків з усіх завантажених порцій (bec368f0): права глядача — на сервері.
  const refs = useMemo(() => refIndex(...(list.data?.pages ?? []).map((p) => p.refs)), [list.data]);

  // Лічильник (LST-15): окремим запитом паралельно з першою порцією; 404 — нічого не видно. На
  // старті застосунку — після межі першого екрана (perf/first-screen.ts): рядкам він не потрібен.
  const firstScreen = useFirstScreenDone();
  const count = useQuery({
    ...viewCountQuery(entity.key, view.id ?? ''),
    enabled: view.id !== undefined && firstScreen,
  });
  // Стан лічильника — `useAreaState`: збій після відповіді лишає попереднє число, а не прибирає
  // його (аудит № 21); без відповіді — нічого, скелетона в лічильника немає.
  const countArea = useAreaState(count, { isEmpty: (c) => c === null });
  // Лічильник рахує подання: з фільтром у чернетці він би збрехав — не показуємо.
  const countText =
    draft.filter !== undefined || draft.q || countArea.view !== 'content'
      ? null
      : countLabel(countArea.data);

  // ---- сортування в шапці і стовпці (LST-02, LST-10): ліниві модулі
  const [sortModule, setSortModule] = useState<SortModule | null>(null);
  const toolsAsked = useRef(false);
  const loadTools = useCallback(() => {
    if (toolsAsked.current) return;
    toolsAsked.current = true;
    Promise.all([loadSortHeader(), loadColumnsPanel()])
      .then(([m]) => setSortModule((cur) => cur ?? m))
      // Мережа чи нова збірка: наступний намір спробує ще раз.
      .catch(() => {
        toolsAsked.current = false;
      });
  }, []);
  useEffect(() => afterPrefetchStart(loadTools), [loadTools]);
  const setSort = useCallback(
    (sort: ViewSort | undefined) => open(view.key, { ...shown, sort }),
    [open, view.key, shown],
  );
  // Ширина стовпця (LST-03): одразу на екрані, на сервер — після паузи. Подання без id шару не має.
  const onResize = useMemo(
    () =>
      view.id
        ? (column: ColumnSpec, width: number | undefined) =>
            saveViewWidth(client, view, column.key, width, () =>
              toast({ text: tl.widthsNotSaved, tone: 'alert', key: 'widths-not-saved' }),
            )
        : undefined,
    [client, view],
  );
  const header = useMemo(
    () =>
      sortModule?.createSortHeader({
        current: params.sort,
        view: view.sort,
        onChange: setSort,
        onResize,
      }),
    [sortModule, params.sort, view.sort, setSort, onResize],
  );
  // Клік по шапці до модуля (легкий режим, перші миті): модуль вантажиться, клік — щойно прийде.
  const pressed = useRef<ColumnSpec | null>(null);
  const onHeaderPress = useCallback(
    (column: ColumnSpec) => {
      if (!column.sortable) return;
      pressed.current = column;
      loadTools();
    },
    [loadTools],
  );
  useEffect(() => {
    const column = pressed.current;
    if (!sortModule || !column) return;
    pressed.current = null;
    sortModule.chooseSort(column, params.sort, view.sort, setSort);
  }, [sortModule, params.sort, view.sort, setSort]);
  // Наведення в легкому режимі нічого не вантажить (lite-mode.md); фокус і клік — так.
  const onIntent = useCallback(
    (kind: 'pointer' | 'focus') => {
      if (kind === 'focus' || !lite) loadTools();
    },
    [lite, loadTools],
  );

  const columnsButton = useRef<HTMLButtonElement>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [ColumnsPanel, setColumnsPanel] = useState(columnsPanelChunk);
  const [HeavyState, setHeavyState] = useState(heavyChunk);
  const titleKey = entity.titleFields[0] ?? '';
  const viewDefault = useMemo(
    () =>
      viewColumns(view, entity)
        .filter((c) => !c.isTitle)
        .map((c) => c.key),
    [view, entity],
  );
  const chosen = useMemo(
    () => source.columns.filter((c) => !c.isTitle).map((c) => c.key),
    [source.columns],
  );
  const setColumns = useCallback(
    (columns: readonly string[] | undefined) => {
      const same = columns !== undefined && columns.join(',') === viewDefault.join(',');
      // Особистий шар подання на сервері (E4a-03): на всіх пристроях людини.
      saveViewColumns(client, view, same ? undefined : columns).catch(() => {
        toast({ text: tl.columnsNotSaved, tone: 'alert' });
      });
    },
    [client, view, viewDefault],
  );
  const personal = params.columns !== undefined || params.sort !== undefined;
  const resetView = useCallback(() => {
    setColumns(undefined);
    open(view.key);
  }, [view.key, setColumns, open]);
  // Старий вибір стовпців з пристрою (W2) — на сервер одноразово, при першому відкритті подання.
  // Збій — тихо: запис лишається на пристрої й діє, наступне відкриття спробує знову.
  useEffect(() => {
    migrateLocalColumns(client, view).catch(() => {});
  }, [client, view]);

  // ---- живе оновлення (LST-18): лінивий модуль, ключ запиту — той, що на екрані
  // Поки список на екрані, подія не перечитує всіх його порцій (рядки не стрибають) і до того, як
  // прийшов модуль: у легкому режимі це перша хвилина.
  useEffect(() => holdListRefresh(entity.key), [entity.key]);
  const [live, setLive] = useState<LiveList | null>(null);
  const snapshot = useSyncExternalStore(
    live?.subscribe ?? noSubscribe,
    live?.getSnapshot ?? noSnapshot,
  );
  useEffect(() => {
    let started: LiveList | null = null;
    let done = false;
    const start = () => {
      if (done || started) return;
      loadLive()
        .then((m) => {
          if (done || started) return;
          started = m.startLiveList({
            client,
            entity: entity.key,
            queryKey: query.queryKey,
            lite,
            countsKey: viewCountsKey(entity.key),
            firstPage: (signal) => fetchListPage(view, entity, pageSize, params, signal),
          });
          setLive(started);
        })
        .catch(() => undefined);
    };
    const cancelLate = lite ? () => undefined : afterPrefetchStart(start);
    const timer = setTimeout(start, LIVE_START_FALLBACK_MS);
    return () => {
      done = true;
      cancelLate();
      clearTimeout(timer);
      started?.stop();
      setLive(null);
    };
  }, [client, entity, view, pageSize, params, query.queryKey, lite]);
  const refresh = useCallback(() => {
    if (!live) return;
    setRefreshTick((n) => n + 1);
    void live.refresh();
  }, [live]);
  const pendingText =
    snapshot.pending > 0 ? tl.refreshChanges(plural(snapshot.pending, tl.changeForms)) : '';
  // Кількість змін — програмі читання з екрана (aria-live), не частіше за дебаунс подій.
  useEffect(() => {
    if (!pendingText) return;
    const timer = setTimeout(() => announce(pendingText), 1000);
    return () => clearTimeout(timer);
  }, [pendingText]);
  const screen = useMemo(
    () => ({
      key: listScreenKey(view.key),
      announce: (row: number, total: number) => n.listReturned(view.label.uk, row, total),
    }),
    [view],
  );
  useSignInRedirect(isUnauthorized(list.error));
  const chips = useMemo(() => chipViews(views, view), [views, view]);
  const todayItems = useTodayToggleItem();
  // Стовпці експорту «як у поданні» (LST-32): лише поля сутності, у порядку таблиці.
  const exportColumns = useMemo(
    () => source.columns.map((c) => c.key).filter((key) => fieldOf(entity, key) !== undefined),
    [source.columns, entity],
  );
  const bar = useViewBar({
    view,
    entity,
    views,
    draft: shown,
    columns: source.columns,
    widths: params.widths,
    permissions: meta.permissions,
    open,
  });

  // ---- фільтр (E4a-W4): діюча умова — чипами, зміна — чернетка подання
  const filterBase = config?.filter;
  const loaded = config !== undefined;
  const setFilter = useCallback(
    (next: FilterNode | null) =>
      open(view.key, withFilterDraft(shown, next, loaded ? { filter: filterBase } : undefined)),
    [open, view.key, shown, loaded, filterBase],
  );
  const targetOf = useCallback((key: string) => entityOf(meta, key), [meta]);
  const filterBar = useFilterBar({
    entity,
    filter: params.filter ?? null,
    onChange: setFilter,
    targetOf,
    lite,
  });
  useEffect(() => afterPrefetchStart(filterBar.preload), [filterBar.preload]);
  const searching = list.isPlaceholderData && list.isFetching;

  const body = (
    <>
      <ViewToolbar
        title={bar.title}
        count={
          countText === null ? null : (
            <span
              className="toolbar__count"
              data-testid="view-count"
              title={
                count.data?.count === null
                  ? tl.countUnknown
                  : countText.endsWith('+')
                    ? tl.countCapped
                    : undefined
              }
            >
              {countText}
            </span>
          )
        }
        layout={
          <ViewSwitch view={view.key} entity={entity} current="list" draft={d} state={state} />
        }
        mark={bar.mark}
        current={view.key}
        chips={[
          ...chips.map((v) => ({
            key: v.key,
            name: v.shortLabel.uk,
            node: (
              <Link
                className={`chip${v.key === view.key ? ' chip--active' : ''}`}
                to="/l/$view"
                params={{ view: v.key }}
              >
                {v.shortLabel.uk}
              </Link>
            ),
            menu: [
              {
                kind: 'radio' as const,
                id: `view:${v.key}`,
                label: v.shortLabel.uk,
                checked: v.key === view.key,
                onSelect: () => void navigate({ to: '/l/$view', params: { view: v.key } }),
              },
            ],
          })),
          // Кошик сутності (DAT-05; trash.html, кадр В1): останнім після подань.
          {
            key: 'trash',
            name: tl.deletedView(entity.label.uk),
            node: (
              <Link
                className="chip list-trash-link"
                to="/trash"
                search={{ entity: entity.key }}
                data-testid="list-trash"
              >
                {tl.deletedView(entity.label.uk)}
              </Link>
            ),
            menu: [
              { kind: 'separator' as const, id: 'sep-trash' },
              {
                kind: 'item' as const,
                id: 'trash',
                label: tl.deletedView(entity.label.uk),
                onSelect: () => void navigate({ to: '/trash', search: { entity: entity.key } }),
              },
            ],
          },
          // Черга дублів сутності (DAT-07): за кошиком, лише з правилами дублів і правом злиття.
          ...(mergeQueueEntities(meta).some((e) => e.key === entity.key)
            ? [
                {
                  key: 'duplicates',
                  name: tl.duplicatesView(entity.label.uk),
                  node: (
                    <Link
                      className="chip list-trash-link"
                      to="/duplicates"
                      search={{ entity: entity.key }}
                      data-testid="list-duplicates"
                    >
                      {tl.duplicatesView(entity.label.uk)}
                    </Link>
                  ),
                  menu: [
                    {
                      kind: 'item' as const,
                      id: 'duplicates',
                      label: tl.duplicatesView(entity.label.uk),
                      onSelect: () =>
                        void navigate({ to: '/duplicates', search: { entity: entity.key } }),
                    },
                  ],
                },
              ]
            : []),
        ]}
        filters={
          params.q ? (
            <>
              <SearchChip
                q={params.q}
                onClear={() => {
                  const { q: _q, ...rest } = shown;
                  open(view.key, rest);
                }}
              />
              {searchNote === 'capped' ? (
                <span className="hint" role="status" data-testid="search-capped">
                  {t.searchCapped}
                </span>
              ) : null}
              {filterBar.chips}
            </>
          ) : (
            filterBar.chips
          )
        }
        actions={[
          ...bar.changeActions,
          ...(todayItems.length > 0
            ? [{ key: 'today', name: tl.todayToggle, node: <TodayToggle />, menu: todayItems }]
            : []),
          ...(snapshot.pending > 0
            ? [
                {
                  key: 'refresh',
                  name: pendingText,
                  node: (
                    <button
                      className="btn btn--sm"
                      type="button"
                      aria-label={pendingText}
                      title={pendingText}
                      onClick={refresh}
                      data-testid="list-refresh"
                    >
                      <i className="ic ic-refresh" aria-hidden="true" />
                      {snapshot.pending}
                    </button>
                  ),
                  menu: [
                    { kind: 'item' as const, id: 'refresh', label: pendingText, onSelect: refresh },
                  ],
                },
              ]
            : []),
          {
            key: 'columns',
            name: tl.columns,
            node: (
              <button
                ref={columnsButton}
                className="btn btn--ghost"
                type="button"
                aria-expanded={columnsOpen}
                aria-haspopup="dialog"
                onClick={() => setColumnsOpen((o) => !o)}
                onPointerEnter={lite ? undefined : loadTools}
                onFocus={loadTools}
                data-testid="columns"
              >
                <i className="ic ic-columns" aria-hidden="true" />
                {tl.columns}
              </button>
            ),
            menu: [
              {
                kind: 'item' as const,
                id: 'columns',
                label: tl.columns,
                onSelect: () => {
                  loadTools();
                  setColumnsOpen(true);
                },
              },
            ],
          },
          ...(canExport && !params.q
            ? [
                {
                  key: 'export',
                  name: tl.exportAll,
                  node: (
                    <button
                      className="btn btn--ghost"
                      type="button"
                      aria-haspopup="dialog"
                      onClick={exportAll}
                      data-testid="list-export"
                    >
                      <i className="ic ic-file" aria-hidden="true" />
                      {tl.exportAll}
                    </button>
                  ),
                  menu: [
                    {
                      kind: 'item' as const,
                      id: 'export',
                      label: tl.exportAll,
                      onSelect: exportAll,
                    },
                  ],
                },
              ]
            : []),
          {
            key: 'view-menu',
            name: tl.viewActions,
            node: bar.menu,
            menu: menuGroup('view-menu', tl.viewActions, bar.menuItems),
          },
          {
            key: 'filter',
            name: filterBar.menuItem.label,
            node: filterBar.button,
            menu: [filterBar.menuItem],
          },
          // Лупа — закріплена, перед основною дією: у «⋯» не йде (SRC-15, рішення власника
          // 2026-10-28), а «⋯» стоїть перед нею на тому самому місці.
          {
            key: 'search',
            name: t.search,
            node: <SearchBox onSearch={(text) => open(view.key, { ...shown, q: text })} />,
            menu: [],
          },
          ...(entity.canCreate
            ? [
                {
                  key: 'create',
                  name: createLabel(entity.label.uk),
                  node: (
                    <button
                      className="btn btn--primary"
                      type="button"
                      aria-haspopup="dialog"
                      onClick={create}
                      data-testid="create"
                    >
                      {createLabel(entity.label.uk)}
                    </button>
                  ),
                  menu: [],
                },
              ]
            : []),
        ]}
        pinned={['create', 'view-save', 'search']}
        priority={LIST_ACTIONS}
      />
      {bar.panels}
      {filterBar.layer}
      {searching && <div className="list-progress" role="progressbar" aria-label={tf.searching} />}
      {columnsOpen && (
        <RetryBoundary
          onRetry={() => setColumnsPanel(() => columnsPanelChunk())}
          fallback={() => null}
        >
          <Suspense fallback={null}>
            <ColumnsPanel
              title={{ key: titleKey, label: fieldLabel(entity, titleKey) }}
              options={entity.fields
                // Відповідальний — UUID у значеннях: імен у сторінці списку ще немає, а UUID
                // людина не бачить (web-card.md, розд. 5). Стовпець — окремим кроком.
                .filter((f) => f.key !== titleKey && f.key !== 'owner')
                .map((f) => ({ key: f.key, label: f.label.uk }))}
              chosen={chosen}
              personal={params.columns !== undefined}
              onChange={setColumns}
              onClose={() => setColumnsOpen(false)}
              anchor={columnsButton}
            />
          </Suspense>
        </RetryBoundary>
      )}
      {(deleting || bulkRun || exporting || work) && (
        <RetryBoundary
          fallback={() => (
            <p className="list-tail list-tail--error" role="alert">
              {t.error}
            </p>
          )}
          onRetry={() => {
            setDeleting(null);
            setBulkRun(null);
            setExporting(null);
            setWork(null);
          }}
        >
          {/* Один лінивий шар дій над записами: видалення одного (DAT-04), масові (LST-27, 28, 31), експорт (LST-32), робота із записом (LST-23). */}
          <Suspense fallback={null}>
            {work && (
              <WorkPanelBody
                panel={work}
                onDone={() => void client.invalidateQueries({ queryKey: query.queryKey })}
                onClose={closeWork}
              />
            )}
            {deleting && (
              <DeleteFlow
                meta={meta}
                entity={entity}
                recordId={deleting.id}
                title={deleting.title}
                onClose={() => setDeleting(null)}
                onDeleted={deleting.done}
              />
            )}
            {bulkRun &&
              (() => {
                const flow = {
                  entity,
                  selection: bulkRun.selection,
                  selected: bulkRun.selected,
                  onStarted: () => {
                    bulkRun.clear();
                    setBulkRun(markRunning(bulkRun.run, true));
                  },
                  onClose: () => setBulkRun(null),
                };
                if (bulkRun.kind === 'edit') return <BulkEditFlow key={bulkRun.run} {...flow} />;
                if (bulkRun.kind === 'transfer') {
                  return <BulkTransferFlow key={bulkRun.run} {...flow} />;
                }
                return <BulkFlow key={bulkRun.run} action="delete" {...flow} />;
              })()}
            {exporting && (
              <ExportFlow
                key={exporting.run}
                entity={entity}
                selection={exporting.selection}
                visible={exportColumns}
                onStarted={() => {
                  exporting.clear();
                  setExporting(markRunning(exporting.run, true));
                }}
                onSettled={() => setExporting(markRunning(exporting.run, false))}
                onClose={() => setExporting(null)}
              />
            )}
          </Suspense>
        </RetryBoundary>
      )}
      <RecordTable
        selectionActions={selectionActions}
        selectable={canDelete || canBulk || canExport}
        selectionScope={JSON.stringify([params.filter ?? null, params.q ?? null])}
        selectionLimit={BULK_IDS_MAX}
        rowMenu={rowMenu}
        onRowMenuOpen={onRowMenuOpen}
        logRow={logRow}
        source={source}
        query={query}
        screen={screen}
        header={header}
        // Намір людини (наведення, фокус на таблиці) вантажить сортування в шапці раніше за
        // простій; у легкому режимі простою немає — фокус і клік по шапці.
        onIntent={onIntent}
        onHeaderPress={onHeaderPress}
        marks={snapshot.marks}
        resetKey={`${params.sort?.field ?? ''}:${params.sort?.dir ?? ''}:${refreshTick}:${params.q ?? ''}:${JSON.stringify(params.filter ?? null)}`}
        total={draft.filter !== undefined || draft.q ? undefined : exactCount(count.data)}
        testId="platform-list"
        // Порожній стан з метаданих (UX-37, LST-35): слова — форми сутності, дія — лише з правом.
        // Поки діалог створення відкритий, порожній стан його не дублює.
        empty={
          params.q && searchNote === 'no_path' ? (
            <div data-testid="empty-state" data-reason="search-unavailable">
              <StateView
                icon="search"
                title={searchQueryTooShort(params.q) ? t.searchTooShort : t.searchNoPath}
              />
            </div>
          ) : params.q ? (
            <EmptyState
              reason="search"
              query={params.q}
              noun={entity.label.uk}
              action={{
                label: t.searchEverywhere,
                onAction: () => openPalette(params.q, undefined, true),
              }}
            />
          ) : creating ? null : (
            <EmptyState
              reason="none"
              noun={entity.label.uk}
              own={view.scope === 'mine'}
              canCreate={entity.canCreate}
              action={{ label: createLabel(entity.label.uk), onAction: create }}
            />
          )
        }
        error={(error, retry) =>
          isUnauthorized(error) ? null : isQueryTimeout(error) ? (
            // «Подання надто важке» (LST-16): лише ця гілка вантажить свій модуль.
            <RetryBoundary
              onRetry={() => setHeavyState(() => heavyChunk())}
              fallback={() => (
                <ErrorState error={error} onRetry={retry} title={t.error} page={false} />
              )}
            >
              {/* Чанк стану «надто важке» — очікування області, а не порожнеча на місці помилки. */}
              <Suspense
                fallback={<Pending skeleton={<SkeletonLines rows={2} />} onRetry={retry} />}
              >
                <HeavyState
                  onRetry={retry}
                  sorted={params.sort !== undefined}
                  onReset={personal ? resetView : undefined}
                />
              </Suspense>
            </RetryBoundary>
          ) : (
            <ErrorState
              error={error}
              onRetry={retry}
              title={t.error}
              text={busyMessage(error) ?? undefined}
              page={false}
            />
          )
        }
      />
    </>
  );
  return <RefsProvider refs={refs}>{body}</RefsProvider>;
}

/** Підпис поля з метаданих; поля немає — ключ (не має статися: поле-назва завжди в `/meta`). */
const fieldLabel = (entity: MetaEntity, key: string) =>
  entity.fields.find((f) => f.key === key)?.label.uk ?? key;

/**
 * Скелетон, якщо перша порція йде довше 300 мс: панель екрана вже з назвою подання (states.md).
 * Фази — маршрутного очікування (`useRoutePending`): з 3 с від переходу «Довше, ніж зазвичай…», з
 * 10 с — тайм-аут зі «Спробувати ще раз»; панель подання лишається.
 */
export function ListSkeleton() {
  const { view } = useParams({ from: '/l/$view' });
  const meta = usePlatformMeta();
  const label = meta.data?.views.find((v) => v.key === view)?.label.uk ?? '';
  const pending = useRoutePending();
  return (
    <>
      <div className="toolbar">
        <h1 className="toolbar__title">{label}</h1>
      </div>
      <div className="content" aria-busy={pending.view === 'skeleton' || undefined}>
        <RoutePending state={pending} skeleton={<SkeletonTable label={label} rows={12} />} />
      </div>
    </>
  );
}
